import type { RegimeTributario } from '@malgoos/shared';
import type { MarcaInadimplencia } from '../../nucleo/inadimplencia';

export interface Tag {
  id: string;
  nome: string;
  cor: string;
}

export interface Parceiro {
  id: string;
  tipo: 'pessoa' | 'empresa';
  nome: string;
  /** Fase 2 (W19, K6): código do parceiro, único por workspace quando preenchido. */
  codigo?: string | null;
  /** Fase 2 (W19, K4): marca derivada do Contas; ausente sem o Contas ou sem `contas.ver`, null quando em dia. */
  inadimplencia?: MarcaInadimplencia | null;
  documento: string | null;
  /** Lote 19/10: tipo do documento fora do Brasil (ein, ssn, itin, cif, nif, nie ou o do país); null no Brasil e no registro livre antigo. */
  documento_tipo?: string | null;
  email: string | null;
  telefone: string | null;
  parent_id: string | null;
  parent_nome: string | null;
  cargo: string | null;
  setor_id: string | null;
  setor_nome: string | null;
  tags: Tag[];
  origem: string;
  ranking_cliente: number;
  ranking_fornecedor: number;
  e_cliente: boolean;
  e_fornecedor: boolean;
  /** Fase 2 (W5): indicador ou parceiro comercial. */
  e_parceiro: boolean;
  /** Trilha 4 (ciclo do cliente): situação comercial de quem é do lado cliente. */
  situacao_comercial: SituacaoComercial;
  /** Fase 2 (W5): país (ISO 3166-1 alfa-2); fora do BR o documento é o registro do país de origem. */
  pais: string;
  moeda_padrao: string | null;
  observacoes: string | null;
  extras: Record<string, unknown>;
  arquivado: boolean;
  /** Destino da mescla (parceiro mesclado não é desarquivado). */
  mesclado_em?: string | null;
  created_at: string;
  /** V1 verticais: as verticais (empresas) do cadastro; vazia = "Todas". */
  empresas?: string[];
}

export interface Endereco {
  id: string;
  tipo: string;
  cep: string | null;
  logradouro: string;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string;
  uf: string | null;
  pais?: string;
}

export interface ContaBancaria {
  id: string;
  banco: string;
  banco_codigo?: string | null;
  agencia: string | null;
  conta: string | null;
  conta_tipo?: 'corrente' | 'poupanca' | 'pagamento' | null;
  chave_pix_tipo?: 'cpf' | 'cnpj' | 'email' | 'telefone' | 'aleatoria' | null;
  chave_pix: string | null;
  titular: string | null;
  titular_documento?: string | null;
  iban?: string | null;
  swift?: string | null;
}

/** Fase 2 (W5): dados fiscais 1:1. */
export interface Fiscal {
  razao_social: string | null;
  nome_fantasia: string | null;
  data_abertura: string | null;
  porte: 'mei' | 'me' | 'epp' | 'demais' | null;
  natureza_juridica: string | null;
  situacao_cadastral: 'ativa' | 'suspensa' | 'inapta' | 'baixada' | 'nula' | null;
  situacao_data: string | null;
  capital_social_centavos: number | null;
  moeda?: string;
  isento_ie: boolean;
  inscricao_estadual: string | null;
  inscricao_municipal: string | null;
  produtor_rural: boolean;
  inscricoes_produtor: string[];
}

export type Regime = RegimeTributario;
export type TipoVinculo = 'socio_de' | 'representante_de' | 'responsavel_por';

export interface Ficha extends Parceiro {
  enderecos: Endereco[];
  contas_bancarias: ContaBancaria[];
  contatos: { id: string; nome: string; cargo: string | null; email: string | null; telefone: string | null; linkedin?: string | null; adicional?: boolean }[];
  projetos: { id: string; nome: string; status: string }[];
  fiscal: Fiscal | null;
  regimes: { id: string; regime: Regime; inicio: string; fim: string | null; vigente: boolean }[];
  vinculos_pessoas: { id: string; tipo: TipoVinculo; direcao: 'saida' | 'entrada'; outro_id: string; outro_nome: string; outro_tipo: string; outro_arquivado: boolean }[];
}

export interface Projeto {
  id: string;
  nome: string;
  cliente_id: string | null;
  cliente_nome: string | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  equipe: string[];
  inicio: string | null;
  prazo: string | null;
  orcamento_centavos: number | null;
  moeda: string;
  status: 'planejado' | 'em_andamento' | 'pausado' | 'concluido' | 'cancelado';
  descricao: string | null;
  extras: Record<string, unknown>;
  atrasado: boolean;
}

/** Filtro da aba "Pessoas e empresas" (fase 2, W5): o mesmo `papel` da listagem da API. */
export type FiltroPapel = 'todos' | 'cliente' | 'fornecedor' | 'parceiro' | 'contato' | 'empresa';
export const SITUACOES_COMERCIAIS = ['lead', 'cliente', 'ex_cliente', 'migrado_sem_contrato'] as const;
export type SituacaoComercial = (typeof SITUACOES_COMERCIAIS)[number];
/** crm8 (2): as situações que aparecem na operação (Cadastros); o lead fica só no CRM. */
export const SITUACOES_DA_OPERACAO = ['cliente', 'ex_cliente', 'migrado_sem_contrato'] as const;
/**
 * crm8 (2): as opções do campo situação no formulário de Cadastros. Sem "Lead" (o lead nasce no CRM e vira cliente no ganho),
 * a não ser que o cadastro aberto já seja lead: aí a opção fica, para o valor gravado aparecer e não mudar sem querer.
 */
export function situacoesDoForm(atual?: string | null): readonly SituacaoComercial[] {
  return atual === 'lead' ? SITUACOES_COMERCIAIS : SITUACOES_DA_OPERACAO;
}

export const FILTROS_PAPEL: readonly FiltroPapel[] = ['todos', 'cliente', 'fornecedor', 'parceiro', 'contato', 'empresa'];

/** Papel escolhido no "Novo" (fase 2, W5). */
export type PapelNovo = 'cliente' | 'fornecedor' | 'parceiro' | 'contato';
export const PAPEIS_NOVO: readonly PapelNovo[] = ['cliente', 'fornecedor', 'parceiro', 'contato'];

/** Papel do "Novo" já marcado pelo filtro ativo (Empresas e Todos começam em Cliente). */
export const papelDoFiltro = (f: FiltroPapel): PapelNovo => (f === 'fornecedor' || f === 'parceiro' || f === 'contato' ? f : 'cliente');

/** Compatibilidade: `Papel` era a aba antiga (Empresas, Clientes, Fornecedores, Contatos). */
export type Papel = 'empresa' | 'cliente' | 'fornecedor' | 'contato';

/** Resposta de `GET /cadastros/consultas/{cnpj|cep}/:valor` (fase 2, W5). */
export interface RespostaConsulta {
  habilitada: boolean;
  situacao: 'encontrado' | 'nao_encontrado' | 'falha' | 'limite' | null;
  dados: Record<string, unknown> | null;
  origem: 'cache' | 'provedor' | null;
}

export { documentoFormatado, telefoneFormatado } from '../../nucleo/formato';
